/**
 * @file ContractDrawerStatusStripContainer.tsx
 * @description Owns the decision logic for the status strip:
 *   - Visibility: the strip is hidden entirely when there is no
 *     extraction OR the drawer is archived.
 *   - Date formatting: converts the raw ISO `savedAt` timestamp to a
 *     short locale date string, returning null for invalid input.
 *   - Edit-button gating: the Edit button is hidden when the caller is
 *     read-only (non-OWNER or archived).
 *
 *   The container returns `null` when the strip should be suppressed so
 *   callers never have to guard at the call site. When visible it
 *   forwards resolved primitive props to the pure
 *   `ContractDrawerStatusStripView`.
 * @module features/contracts/components/ContractDrawerStatusStrip/ContractDrawerStatusStripContainer
 */

import React, { memo } from "react";

import ContractDrawerStatusStripView from "./ContractDrawerStatusStripView";
import { useAppLocale } from "@/i18n";
import { formatDate as formatIntlDate } from "@/utils/intlFormat";

/** Props for {@link ContractDrawerStatusStripContainer}. */
export interface ContractDrawerStatusStripContainerProps {
  /**
   * ISO timestamp the extraction was last saved at, or null when no
   * save has happened yet (PENDING_REVIEW).
   */
  readonly savedAt: string | null;
  /** Whether the drawer has a loaded extraction. */
  readonly hasExtraction: boolean;
  /** Whether the drawer is in archived-read mode (SE-4349). */
  readonly isArchived: boolean;
  /**
   * Whether the caller is read-only (non-OWNER). Hides the Edit button
   * while leaving the badge + date visible so the user can still see
   * when the extraction was last saved.
   */
  readonly isReadOnly: boolean;
  /** Invoked when the user taps the Edit button. */
  readonly onEditPress: () => void;
  /**
   * Optional slot rendered to the left of the Edit button on the
   * strip's right side. Lets the drawer body inline Save / Discard
   * buttons on the same horizontal row (SE-4767).
   */
  readonly extraActions?: React.ReactNode;
}

/**
 * Formats an ISO timestamp as a short locale date (e.g. 6/16/2024).
 * Returns null on empty / invalid input so the container can omit the
 * "Saved {date}" clause entirely rather than rendering "Saved Invalid
 * Date".
 * @param iso - ISO timestamp or null
 * @param locale - The active UI locale
 * @returns A formatted date string or null
 */
export function formatSavedDate(
  iso: string | null,
  locale: string
): string | null {
  if (iso === null || iso === "") return null;
  // SE-7966: this called `toLocaleDateString()` with NO locale argument, which
  // resolves to the DEVICE locale — so two users with the same account language
  // saw different dates depending on the phone they held. It now follows the
  // account's languagePreference.
  return formatIntlDate(iso, locale) || null;
}

/**
 * Container resolves every decision, returns null when suppressed, and
 * otherwise forwards primitive props to the View.
 * @param props - Container props
 * @param props.savedAt - Raw ISO timestamp of last save
 * @param props.hasExtraction - Whether an extraction is loaded
 * @param props.isArchived - Whether the drawer is in archived mode
 * @param props.isReadOnly - Whether the caller is non-OWNER / read-only
 * @param props.onEditPress - Handler for the Edit button
 * @param props.extraActions - Optional slot rendered to the left of
 *   the Edit button (e.g. inline Save / Discard buttons)
 * @returns The rendered strip, or null when it should be suppressed
 */
function ContractDrawerStatusStripContainer({
  savedAt,
  hasExtraction,
  isArchived,
  isReadOnly,
  onEditPress,
  extraActions,
}: ContractDrawerStatusStripContainerProps) {
  const locale = useAppLocale();
  if (!hasExtraction || isArchived) return null;

  return (
    <ContractDrawerStatusStripView
      savedDateLabel={formatSavedDate(savedAt, locale)}
      showEditButton={!isReadOnly}
      onEditPress={onEditPress}
      extraActions={extraActions}
    />
  );
}

ContractDrawerStatusStripContainer.displayName =
  "ContractDrawerStatusStripContainer";

export default memo(ContractDrawerStatusStripContainer);
